import { apiClient } from './api';
import type { TokenResponse, LoginResponse, User, PermissionTuple } from '@/types';

export interface OrganizationInfo {
  name: string;
  display_name?: string;
  username: string;
}

export interface LookupResponse {
  user_exists: boolean;
  organizations?: OrganizationInfo[];
}

export const authService = {
  /**
   * Get login URL from backend (redirects to Casdoor)
   * If organization is provided, returns org-specific login URL for shared applications
   * If email is provided, it will be passed as login_hint to pre-fill the email field
   */
  async getLoginUrl(redirectUrl?: string, organization?: string, email?: string): Promise<string> {
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      redirect_url: redirectUrl || window.location.origin + '/login',
      organization,
      email,
    });
    return response.login_url;
  },

  /**
   * Redirect to Casdoor login page
   * If email is provided, it will pre-fill the email field on the Casdoor login page
   */
  async login(organization?: string, email?: string): Promise<void> {
    const loginUrl = await this.getLoginUrl(undefined, organization, email);
    window.location.href = loginUrl;
  },

  /**
   * Lookup user by email - returns organizations
   */
  async lookupUser(email: string): Promise<LookupResponse> {
    return apiClient.post<LookupResponse>('/auth/lookup', { email });
  },

  /**
   * Login with email and password
   */
  async loginWithPassword(email: string, password: string, organization?: string): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>('/auth/login/password', {
      email,
      password,
      organization,
    });
  },

  /**
   * Forgot password — backend-routed. The MDM backend drives Casdoor
   * server-to-server, so the browser never talks to Casdoor directly (no
   * VITE_CASDOOR_ENDPOINT, no CORS, no cross-site cookie).
   */
  async forgotSend(email: string): Promise<{ message: string }> {
    return apiClient.post('/auth/forgot/send', { email });
  },

  async forgotVerify(email: string, code: string): Promise<{ reset_token: string }> {
    return apiClient.post('/auth/forgot/verify', { email, code });
  },

  async forgotReset(email: string, code: string, resetToken: string, newPassword: string): Promise<{ message: string }> {
    return apiClient.post('/auth/forgot/reset', { email, code, reset_token: resetToken, new_password: newPassword });
  },

  /**
   * Handle OAuth callback - exchange code for tokens
   */
  async handleCallback(code: string, state?: string): Promise<TokenResponse> {
    const response = await apiClient.post<TokenResponse>('/auth/callback', {
      code,
      state,
    });
    return response;
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await apiClient.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response;
  },

  /**
   * Get current user info (requires valid token)
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/auth/me');
  },

  /**
   * Validate SSO token - for auto-login from external apps
   * This allows users who are already logged in to another app
   * (sharing the same Casdoor) to auto-login to Aviron MDM
   */
  async validateSSOToken(token: string): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>('/auth/sso', { token });
  },

  /**
   * Revoke a token (adds to blocklist, clears perm cache)
   */
  async revokeToken(token: string): Promise<void> {
    await apiClient.post('/auth/revoke', { token });
  },

  /**
   * Refresh user permissions from Casdoor (clears cache and re-resolves)
   */
  async refreshPermissions(): Promise<{ permissions: PermissionTuple[]; message: string }> {
    return apiClient.post('/auth/permissions/refresh', {});
  },

  /**
   * Build Casdoor logout URL
   */
  getLogoutUrl(): string {
    const casdoorEndpoint = import.meta.env.VITE_CASDOOR_ENDPOINT || 'http://localhost:8000';
    return `${casdoorEndpoint}/logout`;
  },

  /**
   * Logout - clear local state and optionally redirect to Casdoor logout
   */
  logout(redirectToCasdoor = false): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth-storage');

    if (redirectToCasdoor) {
      window.location.href = this.getLogoutUrl();
    } else {
      window.location.href = '/login';
    }
  },
};

export default authService;
